import apiClient from './apiClient';

// ============================================================
// Types — matching backend Go models (D206 繳庫退出單)
// ============================================================

export interface FD160 {
  fd160_01: string;       // 單號 (PK)
  fd160_10: string;       // 分包商代號
  fd160_11: string;       // 繳庫單號
  fd160_12: string | null; // 退回日期 (ISO string)
  fd160_13: string;       // 出庫庫位
  fd160_14: number;       // 總退回數量
  fd160_15: string;
  fd160_16: number;       // 未稅退貨總額
  fd160_17: boolean;
  fd160_18: string;       // 出庫類別 A=渤揚倉庫 B=工廠
  fd160_19: string;       // 出庫通知單號(已停用)
  fd160_20: number;       // 總項數
  fd160_49: string | null;
  fd160_80: string;       // 狀態 N=未核准 P=已核准
  fd160_89: string;
  fd160_90: number;       // Version
  fd160_91: string;       // 建立人員
  fd160_92: string | null; // 修改日期
  fd160_93: string;       // 修改人員
  fd160_21: string | null; // 建立日期
  fd160_22: number;       // 退貨總稅額
  fd160_94: string;

  // Associations
  details?: FD161[];
}

export interface FD161 {
  fd161_01: string;       // 單號 FK
  fd161_02: number;       // 項次
  fd161_10: string;       // 品號前段
  fd161_11: string;       // 品號後段
  fd161_12: string;       // 批號
  fd161_13: string;       // 色號
  fd161_14: number;       // 進貨單價
  fd161_141: number;      // 備用單價
  fd161_15: number;       // 退出數量
  fd161_16: number;       // 換算率
  fd161_161: number;      // 換算率(備用)
  fd161_17: string;       // 備註
  fd161_18: string;       // 退出單位
  fd161_19: number;
  fd161_20: number;       // 現有庫存量
  fd161_21: string;       // 印花配色
  fd161_22: number;       // 退出金額
  fd161_221: number;
  fd161_90: number;
}

// ============================================================
// Request / Response types
// ============================================================

export interface D206ListResponse {
  data: FD160[];
  total: number;
  page: number;
  size: number;
}

export interface D206CreateRequest {
  master: Partial<FD160>;
  details: Partial<FD161>[];
}

export interface ValidationError {
  field: string;
  rule: string;
  message: string;
}

export interface ApprovalResponse {
  message: string;
  status: string;
}

export interface ApprovalHistory {
  id: number;
  action: string;
  actor_id: string;
  actor_name: string;
  comment: string;
  old_status: string;
  new_status: string;
  created_at: string;
}

// ============================================================
// API methods
// ============================================================

export const d206Api = {
  async list(page = 1, size = 20): Promise<D206ListResponse> {
    const res = await apiClient.get('/api/d206', { params: { page, size } });
    return res.data;
  },

  async get(id: string): Promise<FD160> {
    const res = await apiClient.get(`/api/d206/${encodeURIComponent(id)}`);
    return res.data;
  },

  async previewFromReceiptStock(receiptNo: string, warehouse: string): Promise<FD160> {
    const res = await apiClient.get(
      `/api/d206/from-receipt/${encodeURIComponent(receiptNo)}/warehouse/${encodeURIComponent(warehouse)}`,
    );
    return res.data;
  },

  async create(data: D206CreateRequest): Promise<FD160> {
    const res = await apiClient.post('/api/d206', data);
    return res.data;
  },

  async update(id: string, data: D206CreateRequest): Promise<FD160> {
    const res = await apiClient.put(`/api/d206/${encodeURIComponent(id)}`, data);
    return res.data;
  },

  async delete(id: string): Promise<void> {
    await apiClient.delete(`/api/d206/${encodeURIComponent(id)}`);
  },

  // ── Approval workflow ──

  async submitForApproval(id: string, comment?: string): Promise<ApprovalResponse> {
    const res = await apiClient.post(`/api/d206/${encodeURIComponent(id)}/submit`, { comment: comment || '' });
    return res.data;
  },

  async approve(id: string, comment?: string): Promise<ApprovalResponse> {
    const res = await apiClient.post(`/api/d206/${encodeURIComponent(id)}/approve`, { comment: comment || '' });
    return res.data;
  },

  async reject(id: string, comment?: string): Promise<ApprovalResponse> {
    const res = await apiClient.post(`/api/d206/${encodeURIComponent(id)}/reject`, { comment: comment || '' });
    return res.data;
  },

  async writeoff(id: string, comment?: string): Promise<ApprovalResponse> {
    const res = await apiClient.post(`/api/d206/${encodeURIComponent(id)}/writeoff`, { comment: comment || '' });
    return res.data;
  },

  async getApprovalHistory(id: string): Promise<ApprovalHistory[]> {
    const res = await apiClient.get(`/api/d206/${encodeURIComponent(id)}/approval-history`);
    return res.data.data || [];
  },
};
